//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { QueryRenderer, ReadyState } from "react-relay";
import { connect } from "react-redux";
// autopilot-status imports
import { IRendererContainer, IRendererProps, IRendererState } from "./AutopilotStatus.d";
import { AutopilotStatus } from "./AutopilotStatus";
import { query } from "./AutopilotStatus.gql";
// util
import { getRenderTimeZone } from "@util/time-zone.util";
import { handleReadyStateError } from "@util/relay.util";
// state
import { IReduxState } from "@reducers/root.reducer";
import {
  lastDefragCardTimestamp,
  lastAutopilotSuggestionsCardTimestamp,
} from "@state/local-storage";

//////////////////
// COMPONENT
//////////////////
class AutopilotStatusRendererComponent extends React.PureComponent<IRendererProps, IRendererState> {
  private refetchInterval?: ReturnType<typeof setInterval>;
  private shouldForce = false;

  private cachedOrg?: ISchema.IOrg;
  private cachedUser?: ISchema.IUser;

  constructor(props: IRendererProps) {
    super(props);
    this.state = {
      key: 0,
    };
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public componentDidMount() {
    // if chrome extension, check open state to conditionally set interval, otherwise set interval
    this.handleChromeExtensionOpenStateChange(
      this.props.isChromeExtension ? this.props.chromeExtensionOpen : true,
    );
  }

  public componentWillUnmount() {
    this.clearRefetchInterval();
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IRendererProps) {
    if (
      nextProps.isChromeExtension &&
      nextProps.chromeExtensionOpen !== this.props.chromeExtensionOpen
    ) {
      this.handleChromeExtensionOpenStateChange(nextProps.chromeExtensionOpen);
    }

    return nextProps.orgRelayId !== this.props.orgRelayId;
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private clearRefetchInterval = () => {
    if (this.refetchInterval) {
      clearInterval(this.refetchInterval);
      delete this.refetchInterval;
    }
  };

  private setRefetchInterval = () => {
    this.clearRefetchInterval();
    this.refetchInterval = setInterval(() => {
      this.refetch();
    }, 30 * 60 * 1000);
  };

  private handleChromeExtensionOpenStateChange = (chromeExtensionOpen: boolean) => {
    // if closed, clear interval otherwise refetch + set interval
    if (!chromeExtensionOpen) {
      this.clearRefetchInterval();
    } else {
      this.refetch();
      this.setRefetchInterval();
    }
  };

  private refetch = () => {
    this.shouldForce = true;
    this.setState({ key: this.state.key + 1 });
  };

  private calculateBadgeCount = (org: ISchema.IOrg) => {
    const { tailoredDefragCardSummary } = org;
    const showDefragCard =
      tailoredDefragCardSummary &&
      tailoredDefragCardSummary.__typename !== "GraphEntityError" &&
      tailoredDefragCardSummary.yourEventMoves &&
      tailoredDefragCardSummary.yourEventMoves.length > 0 &&
      parseInt(lastDefragCardTimestamp.get() || "0") < tailoredDefragCardSummary.defragTime;

    const showAutopilotSuggestionsCard =
      tailoredDefragCardSummary &&
      tailoredDefragCardSummary.__typename !== "GraphEntityError" &&
      tailoredDefragCardSummary.knobs.inviteOrganizerKnobs.length +
        tailoredDefragCardSummary.knobs.autopilotMeetingKnobs.length +
        tailoredDefragCardSummary.knobs.lessenAutopilotConstraintKnobs.length >
        0 &&
      parseInt(lastAutopilotSuggestionsCardTimestamp.get() || "0") <
        tailoredDefragCardSummary.defragTime;

    const shouldNotifyExtensionOfDefragSummaryCard =
      !!showDefragCard &&
      (tailoredDefragCardSummary as ISchema.ITailoredDefragCardSummary).defragTime >
        this.props.lastViewedFeedMillis;

    const shouldNotifyExtensionOfAutopilotSuggestionsCard =
      !!showAutopilotSuggestionsCard &&
      (tailoredDefragCardSummary as ISchema.ITailoredDefragCardSummary).defragTime >
        this.props.lastViewedFeedMillis;

    return shouldNotifyExtensionOfDefragSummaryCard &&
      shouldNotifyExtensionOfAutopilotSuggestionsCard
      ? 2
      : shouldNotifyExtensionOfDefragSummaryCard || shouldNotifyExtensionOfAutopilotSuggestionsCard
      ? 1
      : 0;
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    const { environment, orgRelayId, orgTimeZone, fastCrawlFinished, isActionable } = this.props;

    if (!orgTimeZone || !fastCrawlFinished) {
      return null;
    }

    return (
      <QueryRenderer
        key={this.state.key}
        environment={environment}
        query={query}
        variables={{ orgRelayId, timeZone: getRenderTimeZone() }}
        cacheConfig={{ force: this.shouldForce }}
        render={(readyState: ReadyState) => {
          // check for error or show a loader for first load
          if (readyState.error) {
            handleReadyStateError(readyState.error, "AutopilotStatusRenderer");

            return null;
          }

          const org = readyState && readyState.props && (readyState.props.org as ISchema.IOrg);
          const user =
            readyState &&
            readyState.props &&
            readyState.props.viewer &&
            (readyState.props.viewer.user as ISchema.IUser);

          if (org && user) {
            this.cachedOrg = org;
            this.cachedUser = user;
          }

          const extensionNotificationCount = this.cachedOrg
            ? this.calculateBadgeCount(this.cachedOrg)
            : 0;

          this.shouldForce = false;
          return (
            <AutopilotStatus
              isActionable={isActionable}
              org={this.cachedOrg || null}
              user={this.cachedUser || null}
              orgTimeZone={this.props.orgTimeZone}
              badgeCount={extensionNotificationCount}
            />
          );
        }}
      />
    );
  }
}

///////////
// Redux //
///////////
function mapStateToProps(state: IReduxState, _ownProps: IRendererContainer) {
  return {
    isChromeExtension: state.webExtension.isWebExtension,
    chromeExtensionOpen: state.webExtension.webExtensionOpen,
    lastViewedFeedMillis: state.chromeFeed.lastViewedFeedMillis,
  };
}

export const AutopilotStatusRenderer = connect(
  mapStateToProps,
  {},
)(AutopilotStatusRendererComponent);
